import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import {
  getCustomers,
  getCustomerSessionsByCompanyId,
} from '../firebase/firestore';
import { CustomerRecord } from '../firebase/types';
import CompanyStatusIcon from '../components/CompanyStatusIcon';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

const AdminCompaniesPage: React.FC = () => {
  const { logout, user, adminUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState(''); // 実際に検索を実行したキーワード
  const [companies, setCompanies] = useState<CustomerRecord[]>([]);
  const [companiesWithSessions, setCompaniesWithSessions] = useState<
    Set<string>
  >(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageHistory, setPageHistory] = useState<QueryDocumentSnapshot[]>([]);
  const searchResultsCacheRef = useRef<CustomerRecord[][]>([]); // ページごとの結果を保持（配列のインデックス = ページ番号 - 1）
  const [searchHasMore, setSearchHasMore] = useState(true); // 検索結果に続きがあるか

  const loadCompanies = useCallback(
    async (startAfterDoc?: QueryDocumentSnapshot) => {
      try {
        setIsLoading(true);
        setError('');
        const result = await getCustomers(50, startAfterDoc);
        setCompanies(result.customers);
        setLastDoc(result.lastDoc);
        setHasMore(result.customers.length === 50);

        // 各会社のセッション有無を確認
        const sessionChecks = await Promise.all(
          result.customers.map(async company => {
            try {
              const sessions = await getCustomerSessionsByCompanyId(
                company.id,
                1
              );
              return {
                companyId: company.id,
                hasSessions: sessions.length > 0,
              };
            } catch {
              return { companyId: company.id, hasSessions: false };
            }
          })
        );

        const companiesWithSessionsSet = new Set<string>();
        sessionChecks.forEach(check => {
          if (check.hasSessions) {
            companiesWithSessionsSet.add(check.companyId);
          }
        });
        setCompaniesWithSessions(companiesWithSessionsSet);
      } catch (err) {
        setError('企業データの取得に失敗しました');
        // eslint-disable-next-line no-console
        console.error('Error loading companies:', err);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // 検索キーワードがある場合の検索処理
  // pageIndex: ページインデックス（0から開始、0=1ページ目、1=2ページ目）
  const loadSearchResults = useCallback(
    async (searchKeyword: string, pageIndex: number) => {
      try {
        // キャッシュに既に存在する場合は、API呼び出しをスキップ
        if (searchResultsCacheRef.current[pageIndex]) {
          setCompanies(searchResultsCacheRef.current[pageIndex]);
          setCurrentPage(pageIndex + 1);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        setError('');

        // 前のページの最後のドキュメントIDを取得（カーソル用）
        let startAfterDocId: string | null = null;
        if (pageIndex > 0 && searchResultsCacheRef.current[pageIndex - 1]) {
          const prevPageResults = searchResultsCacheRef.current[pageIndex - 1];
          if (prevPageResults.length > 0) {
            startAfterDocId = prevPageResults[prevPageResults.length - 1].id;
          }
        }

        const searchCustomersFunction = httpsCallable<
          {
            searchTerm: string;
            limit?: number;
            startAfter?: string | null;
            collectionPrefix?: string;
          },
          {
            success: boolean;
            data?: {
              customers: CustomerRecord[];
              lastDoc: string | null;
              hasMore: boolean;
              totalCount: number;
            };
            error?: string;
          }
        >(functions, 'searchCustomers');

        const collectionPrefix =
          process.env.REACT_APP_FIREBASE_COLLECTION_PREFIX || '';
        const result = await searchCustomersFunction({
          searchTerm: searchKeyword,
          limit: 50,
          startAfter: startAfterDocId,
          collectionPrefix,
        });

        if (result.data.success && result.data.data) {
          const { customers: searchResults, hasMore: hasMoreResults } =
            result.data.data;

          // キャッシュに追加
          const newCache = [...searchResultsCacheRef.current];
          newCache[pageIndex] = searchResults;
          searchResultsCacheRef.current = newCache;

          setCompanies(searchResults);
          setSearchHasMore(hasMoreResults);
          setCurrentPage(pageIndex + 1);

          // 各会社のセッション有無を確認
          const sessionChecks = await Promise.all(
            searchResults.map(async company => {
              try {
                const sessions = await getCustomerSessionsByCompanyId(
                  company.id,
                  1
                );
                return {
                  companyId: company.id,
                  hasSessions: sessions.length > 0,
                };
              } catch {
                return { companyId: company.id, hasSessions: false };
              }
            })
          );

          const companiesWithSessionsSet = new Set<string>();
          sessionChecks.forEach(check => {
            if (check.hasSessions) {
              companiesWithSessionsSet.add(check.companyId);
            }
          });
          setCompaniesWithSessions(companiesWithSessionsSet);
        } else {
          setError('検索に失敗しました');
          setCompanies([]);
          setSearchHasMore(false);
        }
      } catch (err) {
        setError('検索に失敗しました');
        // eslint-disable-next-line no-console
        console.error('Error searching companies:', err);
        setCompanies([]);
        setSearchHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // 初期ロード時のみ通常の読み込み
  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 検索実行処理
  const handleSearch = useCallback(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm) {
      // 検索キーワードが変更された場合は最初のページから検索
      searchResultsCacheRef.current = [];
      setActiveSearchTerm(trimmedSearchTerm);
      setCurrentPage(1);
      loadSearchResults(trimmedSearchTerm, 0);
    } else {
      // 検索キーワードがクリアされた場合、通常の読み込みに戻す
      searchResultsCacheRef.current = [];
      setActiveSearchTerm('');
      setCurrentPage(1);
      loadCompanies();
    }
  }, [searchTerm, loadSearchResults, loadCompanies]);

  // 検索クリア処理
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setActiveSearchTerm('');
    searchResultsCacheRef.current = [];
    setCurrentPage(1);
    loadCompanies();
  }, [loadCompanies]);

  const handleNextPage = () => {
    if (activeSearchTerm) {
      // 検索結果のページネーション
      const nextPageIndex = currentPage; // 現在のページ番号が次のページのインデックス
      if (searchHasMore || searchResultsCacheRef.current[nextPageIndex]) {
        loadSearchResults(activeSearchTerm, nextPageIndex);
      }
    } else {
      // 通常のページネーション
      if (lastDoc && hasMore) {
        setPageHistory(prev => [...prev, lastDoc]);
        setCurrentPage(prev => prev + 1);
        loadCompanies(lastDoc);
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      if (activeSearchTerm) {
        // 検索結果のページネーション（キャッシュから取得）
        const prevPageIndex = currentPage - 2; // 前のページのインデックス（0から開始）
        // キャッシュに存在する場合はAPI呼び出しなし
        if (searchResultsCacheRef.current[prevPageIndex]) {
          setCompanies(searchResultsCacheRef.current[prevPageIndex]);
          setCurrentPage(prevPageIndex + 1);
        } else {
          // キャッシュにない場合（通常は発生しない）
          loadSearchResults(activeSearchTerm, prevPageIndex);
        }
      } else {
        // 通常のページネーション
        const newHistory = [...pageHistory];
        const prevDoc = newHistory.pop();
        setPageHistory(newHistory);
        setCurrentPage(prev => prev - 1);
        if (prevDoc) {
          loadCompanies(prevDoc);
        } else {
          loadCompanies();
        }
      }
    }
  };

  // エンターキー押下時の検索実行
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCompanySelect = (companyId: string) => {
    window.location.href = `/admin/dashboard/${companyId}`;
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/admin/login';
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-100">
        {/* ヘッダー */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    採用戦略分析システム
                  </h1>
                  <p className="text-sm text-gray-600">
                    管理者ダッシュボード - {user?.email || adminUser?.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>ログアウト</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="会社名で検索..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{ paddingLeft: '3rem' }}
                  className="w-full pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label="会社名で検索"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                検索
              </button>
              {activeSearchTerm && (
                <button
                  onClick={handleClearSearch}
                  disabled={isLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  クリア
                </button>
              )}
            </div>
            {activeSearchTerm && (
              <div className="mt-2 text-sm text-gray-600">
                検索キーワード:{' '}
                <span className="font-medium">{activeSearchTerm}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">読み込み中...</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                      会社ID
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                      会社名
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-700 border-b border-gray-200">
                      ステータス
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {companies.length > 0 ? (
                    companies.map((company, index) => (
                      <tr
                        key={company.id}
                        className={
                          index !== companies.length - 1
                            ? 'border-b border-gray-200'
                            : ''
                        }
                      >
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {company.customerData.displayId || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {companiesWithSessions.has(company.id) ? (
                            <button
                              onClick={() => handleCompanySelect(company.id)}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
                            >
                              {company.customerData.companyName}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-900">
                              {company.customerData.companyName}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <CompanyStatusIcon
                            status={company.customerData.status}
                            size="sm"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        検索結果が見つかりませんでした
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* ページネーション */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || isLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    前へ
                  </button>
                  <span className="text-sm text-gray-600">
                    ページ {currentPage}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={
                      isLoading ||
                      (activeSearchTerm ? !searchHasMore : !hasMore)
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    次へ
                  </button>
                </div>
                <div className="text-sm text-gray-500">
                  {companies.length}件表示
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default AdminCompaniesPage;
